import { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import { useCloudSignInfo } from '@/hooks/useCloudSignInfo';
import { useActiveStep } from '../hooks/useActiveStep';
import NotConfirmedDialog from './NotConfirmedDialog';
import { fetchAuthSession } from 'aws-amplify/auth';
import { updatePatient } from '@/graphql/mutations';
import { UpdatePatientMutation } from '@/API';
import { executeQuery } from '@/client/graphql';
import { notMatchCustomErrorMessage } from '@/lib/sentryClient';
import * as Sentry from '@sentry/react';
import { executePost } from '@/client/rest';

export function ConfirmationConsent() {
  const { cloudSignInfo } = useCloudSignInfo();
  const [openDialog, setOpenDialog] = useState(false);
  const [isOpenAgreeDialog, setIsOpenAgreeDialog] = useState(false);
  const { activeStep, setActiveStep } = useActiveStep();

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleDisagree = async () => {
    try {
      const session = await fetchAuthSession();
      if (session.userSub) {
        if (activeStep === 'ConfirmationComprehensiveConsent') {
          // 包括同意をFalseで更新
          const result = await executeQuery<UpdatePatientMutation>(updatePatient, {
            input: {
              id: session.userSub,
              houkatuDoui: false
            }
          });
          if (result == null) {
            setActiveStep('MedicalInterview');
          }
          setActiveStep('IntroductionOtherHospital');
        }
        if (activeStep === 'ConfirmationTransactionAgreement') {
          // 取引同意をFalseで更新
          const result = await executeQuery<UpdatePatientMutation>(updatePatient, {
            input: {
              id: session.userSub,
              torihikiGoui: false
            }
          });

          setActiveStep('MedicalInterview');
        }
      }

      setOpenDialog(false);
    } catch (e) {
      //失敗時
      setOpenDialog(false);
      setActiveStep('MedicalInterview');
    }
  };

  const handleConfirmationClick = async () => {
    if (cloudSignInfo?.token && cloudSignInfo.documentId) {
      try {
        const postTokenResult = executePost({
          apiName: 'medirecoWebApi',
          path: '/cloud-sign/certificate',
          options: {
            body: {
              token: cloudSignInfo.token,
              documentId: cloudSignInfo.documentId
            }
          }
        }) as any;

        const session = await fetchAuthSession();
        //成功時
        if (postTokenResult.status === 200 && activeStep === 'ConfirmationComprehensiveConsent') {
          // 包括同意をTrueで更新
          await executeQuery<UpdatePatientMutation>(updatePatient, {
            input: {
              input: {
                id: session.userSub,
                houkatuDoui: true
              }
            }
          });

          if (session.userSub) {
            await executeQuery<UpdatePatientMutation>(updatePatient, {
              input: {
                input: {
                  id: session.userSub,
                  houkatuDoui: true
                }
              }
            });
          }

          setActiveStep('MedicalInterview'); // 取引合意が行われるようになったら setActiveStep('TransactionDescription') にする
        } else if (postTokenResult.status === 200 && activeStep === 'ConfirmationTransactionAgreement') {
          // 取引同意をTrueで更新
          if (session.userSub) {
            await executeQuery<UpdatePatientMutation>(updatePatient, {
              input: {
                input: {
                  id: session.userSub,
                  torihikiGoui: true
                }
              }
            });
          }

          setActiveStep('MedicalInterview');
        } else {
          setIsOpenAgreeDialog(true);
        }
      } catch (e) {
        if (e instanceof Error && notMatchCustomErrorMessage(e.name)) {
          Sentry.captureException(e);
        }

        //失敗時
        setIsOpenAgreeDialog(true);
      }
    }
  };

  return (
    <Container maxWidth="md">
      <Box sx={{ maxWidth: 720, margin: 'auto', textAlign: 'center', p: 2 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
          ありがとうございます。
          <br />
          クラウドサイン（support@cloudsign.jp）より確認依頼メールが
          <br />
          ご登録頂きましたメールアドレス宛に届きます。
          <br />
          メールの「書類を確認する」ボタンより契約内容をご確認いただき、
          <br />
          内容に同意してください。
          <br />
          同意後、以下の確認ボタンをクリックしてください。
        </Typography>

        <Button
          type="submit"
          variant="contained"
          onClick={handleConfirmationClick}
          sx={{
            fontSize: '1.25rem',
            padding: '12px 48px',
            minWidth: '300px'
          }}
        >
          確認
        </Button>

        <Typography variant="caption" display="block" gutterBottom>
          ※「確認」ボタンをクリックすると同意内容を確認し、次の画面に進みます。
        </Typography>

        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
          同意いただけない場合は、「同意しない」ボタンよりお進みください。
        </Typography>
        <Box display="flex" justifyContent="space-between" mt={2}>
          <Button
            variant="outlined"
            fullWidth
            onClick={() => setActiveStep('ComprehensiveConsent')}
            sx={{ mt: 3, mb: 2, margin: 2 }}
          >
            戻る
          </Button>
          <Button
            type="submit"
            fullWidth
            variant="contained"
            onClick={handleOpenDialog}
            sx={{ mt: 3, mb: 2, margin: 2 }}
          >
            同意しない
          </Button>
        </Box>
      </Box>
      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>確認</DialogTitle>
        <DialogContent>
          <DialogContentText>
            個人管理サービスを利用されない場合、AIを利用した診療は行われず、提供できる医療が少なくなり、品質が低下する場合がありますがよろしいでしょうか？
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} variant="contained">
            キャンセル
          </Button>
          <Button onClick={handleDisagree}>同意しない</Button>
        </DialogActions>
      </Dialog>
      {activeStep === 'ConfirmationComprehensiveConsent' ? (
        <NotConfirmedDialog
          isOpen={isOpenAgreeDialog}
          setIsOpen={setIsOpenAgreeDialog}
          title="同意が確認できません"
          description="包括同意書に同意してください"
        />
      ) : activeStep === 'ConfirmationTransactionAgreement' ? (
        <NotConfirmedDialog
          isOpen={isOpenAgreeDialog}
          setIsOpen={setIsOpenAgreeDialog}
          title="同意が確認できません"
          description="取引同意書に同意してください"
        />
      ) : null}
    </Container>
  );
}
